import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronDown, Gem, Zap, Palette, Settings, Download, Check } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { Link } from 'react-router-dom';
// Images
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/bwQjjTaBFPGmsVMM5LvtNo/_DSC8285.webp';
const INTERIOR_1 = 'https://cdn.magicpatterns.com/uploads/pKPzBNwMUWtZUzQjDCs91d/6842e88f2f523e985c5fa1a4__DSC8349-2.jpg';
const INTERIOR_2 = 'https://cdn.magicpatterns.com/uploads/3xL1jkjmSyiggyKFQmbzxH/_DSC8261.webp';
const EXTERIOR_1 = 'https://cdn.magicpatterns.com/uploads/h58wUekQZycBGPqmB5effo/682effc0ba43af376950f23b_renders_robeta.jpg';
const DETAIL_1 = 'https://cdn.magicpatterns.com/uploads/iSsLTMw4wKgenTWkbWEAxu/_DSC8170.webp';
const DETAIL_2 = 'https://cdn.magicpatterns.com/uploads/vBzCByw2N7rP6MS6jLeNiZ/_DSC8493.webp';
const DETAIL_3 = 'https://cdn.magicpatterns.com/uploads/pPR23bNLSAVudMWhF2ynaG/_DSC8241.webp';
const DETAIL_4 = 'https://cdn.magicpatterns.com/uploads/7z3is2BCgabuUutCmURgeJ/_DSC8265.webp';
const SECTIONS = [{
  id: 'overview',
  label: 'Overview'
}, {
  id: 'floorplan',
  label: 'Floor Plan'
}, {
  id: '3dview',
  label: '3D View'
}, {
  id: 'gallery',
  label: 'Gallery'
}, {
  id: 'highlights',
  label: 'Highlights'
}, {
  id: 'specifications',
  label: 'Specifications'
}];
export function Mythos4TwoPage() {
  useSEO({
    title: 'Mythos 4 Two — European Craftsmanship Meets North American Freedom',
    description: 'The Mythos 4 Two by Robeta. A luxury Class B camper van on the Mercedes-Benz Sprinter, featuring Air Furniture Technology, premium interiors, and off-grid capability.'
  });
  const [activeSection, setActiveSection] = useState('overview');
  const [activeTab, setActiveTab] = useState('interior');
  const [openAccordion, setOpenAccordion] = useState<string | null>('sleeping');
  // Scroll spy for active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200; // Offset
      SECTIONS.forEach(section => {
        const element = document.getElementById(section.id);
        if (element) {
          const {
            offsetTop,
            offsetHeight
          } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
          }
        }
      });
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 100,
        behavior: 'smooth'
      });
    }
  };
  return <main className="bg-white min-h-screen" data-id="element-679">
      {/* 1. HERO */}
      <section className="relative min-h-screen w-full flex items-end pb-24 md:pb-32 overflow-hidden" data-id="element-680">
        <div className="absolute inset-0 z-0" data-id="element-681">
          <img src={HERO_IMAGE} alt="Mythos 4 Two Exterior" className="w-full h-full object-cover" data-id="element-682" />
          <div className="absolute inset-0 bg-black/30" data-id="element-683" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" data-id="element-684" />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-685">
          <motion.div initial={{
          opacity: 0,
          y: 60
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1
        }} className="max-w-5xl" data-id="element-686">
            <h1 className="text-6xl md:text-8xl lg:text-9xl font-bold text-white uppercase tracking-editorial leading-none mb-6" data-id="element-687">
              Mythos
              <br data-id="element-688" />4 Two
            </h1>
            <p className="text-xl md:text-2xl font-light text-white/80 max-w-xl mb-12 leading-relaxed" data-id="element-689">
              European craftsmanship. North American freedom.
            </p>

            <div className="flex flex-col sm:flex-row items-start gap-6 mb-16" data-id="element-690">
              <Link to="/dealer-network" className="px-10 py-4 bg-white text-robeta-black font-bold uppercase tracking-widest hover:bg-robeta-mist transition-colors min-w-[200px] text-center" data-id="element-691">
                Find a Dealer
              </Link>
              <Link to="/contact" className="px-10 py-4 border border-white text-white font-bold uppercase tracking-widest hover:bg-white/10 transition-colors min-w-[200px] text-center" data-id="element-692">
                Request Info
              </Link>
            </div>

            <div className="border-t border-white/20 pt-6 flex gap-8 text-white/50 text-xs uppercase tracking-widest font-medium" data-id="element-693">
              <span data-id="element-694">6.36m Length</span>
              <span data-id="element-695">Sleeps 2</span>
              <span data-id="element-696">Mercedes-Benz Sprinter</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. STICKY SUB-NAV */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-100 shadow-sm" data-id="element-697">
        <div className="container mx-auto px-6 md:px-12 overflow-x-auto no-scrollbar" data-id="element-698">
          <div className="flex space-x-8 md:space-x-12 py-4 min-w-max" data-id="element-699">
            {SECTIONS.map(section => <button key={section.id} onClick={() => scrollToSection(section.id)} className={`text-xs font-bold uppercase tracking-widest transition-colors pb-1 border-b-2 ${activeSection === section.id ? 'text-robeta-copper border-robeta-copper' : 'text-robeta-black/40 border-transparent hover:text-robeta-black'}`} data-id="element-700">
                {section.label}
              </button>)}
          </div>
        </div>
      </div>

      {/* 3. OVERVIEW */}
      <section id="overview" className="py-24 md:py-32 bg-white" data-id="element-701">
        <div className="container mx-auto px-6 md:px-12" data-id="element-702">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-16" data-id="element-703">
            {[{
            icon: Gem,
            title: 'Luxury Craftsmanship',
            desc: 'Hand-assembled in Slovenia with meticulous attention to every detail.'
          }, {
            icon: Zap,
            title: 'Off-Grid Capability',
            desc: 'Advanced electrical systems for extended off-grid adventures.'
          }, {
            icon: Palette,
            title: 'European Design',
            desc: 'Yacht-inspired interiors with premium materials throughout.'
          }, {
            icon: Settings,
            title: 'Advanced Engineering',
            desc: 'Precision-engineered systems for reliability in any climate.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="flex flex-col items-start p-8 border border-robeta-black/5 hover:border-robeta-copper/30 transition-colors duration-300" data-id="element-704">
                <item.icon className="w-8 h-8 text-robeta-copper mb-6 stroke-[1.5]" data-id="element-705" />
                <h3 className="text-xl font-bold text-robeta-black uppercase tracking-editorial mb-4" data-id="element-706">
                  {item.title}
                </h3>
                <p className="text-robeta-black/60 font-light leading-relaxed" data-id="element-707">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 4. FLOOR PLAN */}
      <section id="floorplan" className="py-24 md:py-32 bg-robeta-mist/10" data-id="element-708">
        <div className="container mx-auto px-6 md:px-12" data-id="element-709">
          <div className="text-center mb-16" data-id="element-710">
            <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-4" data-id="element-711">
              Layout
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-robeta-black uppercase tracking-editorial" data-id="element-712">
              Intelligent Design
            </h2>
          </div>

          <div className="flex flex-col lg:flex-row gap-16 items-center" data-id="element-713">
            {/* Placeholder Floor Plan */}
            <div className="w-full lg:w-3/5 aspect-[16/9] bg-white border border-robeta-black/5 flex items-center justify-center relative overflow-hidden" data-id="element-714">
              <div className="absolute inset-0 bg-[radial-gradient(#bd9c83_1px,transparent_1px)] [background-size:20px_20px] opacity-10" data-id="element-715" />
              <span className="text-robeta-black/20 font-bold uppercase tracking-widest text-xl" data-id="element-716">
                Floor Plan Diagram
              </span>
            </div>

            {/* Accordion */}
            <div className="w-full lg:w-2/5 space-y-4" data-id="element-717">
              {[{
              id: 'sleeping',
              title: 'Sleeping Configuration',
              content: 'Permanent rear bed with premium mattress ensuring restful sleep without daily setup.'
            }, {
              id: 'kitchen',
              title: 'Kitchen Layout',
              content: 'Full galley with two-burner stove, sink, and refrigerator designed for culinary creativity.'
            }, {
              id: 'bathroom',
              title: 'Bathroom Layout',
              content: 'Enclosed wet bath with shower, toilet, and vanity maximizing space and privacy.'
            }, {
              id: 'storage',
              title: 'Storage Solutions',
              content: 'Overhead cabinets, under-bed garage, and modular storage for all your gear.'
            }].map(item => <div key={item.id} className="border-b border-robeta-black/10 pb-4" data-id="element-718">
                  <button onClick={() => setOpenAccordion(openAccordion === item.id ? null : item.id)} className="w-full flex justify-between items-center py-4 text-left group" data-id="element-719">
                    <span className={`text-sm font-bold uppercase tracking-widest transition-colors ${openAccordion === item.id ? 'text-robeta-copper' : 'text-robeta-black group-hover:text-robeta-copper'}`} data-id="element-720">
                      {item.title}
                    </span>
                    <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${openAccordion === item.id ? 'rotate-180 text-robeta-copper' : 'text-robeta-black/30'}`} data-id="element-721" />
                  </button>
                  <AnimatePresence data-id="element-722">
                    {openAccordion === item.id && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="overflow-hidden" data-id="element-723">
                        <p className="text-robeta-black/60 font-light pb-4 leading-relaxed" data-id="element-724">
                          {item.content}
                        </p>
                      </motion.div>}
                  </AnimatePresence>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* 5. 3D VIEW */}
      <section id="3dview" className="py-24 md:py-32 bg-robeta-black text-white" data-id="element-725">
        <div className="container mx-auto px-6 md:px-12 text-center" data-id="element-726">
          <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-4" data-id="element-727">
            360° Experience
          </span>
          <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-editorial mb-12" data-id="element-728">
            Explore Every Angle
          </h2>

          <div className="w-full min-h-[500px] border border-white/10 flex items-center justify-center bg-white/5 relative" data-id="element-729">
            <div className="text-center" data-id="element-730">
              <Settings className="w-12 h-12 text-white/20 mx-auto mb-4 animate-spin-slow" data-id="element-731" />
              <span className="text-white/40 font-light uppercase tracking-widest" data-id="element-732">
                3D Viewer — Coming Soon
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* 6. GALLERY */}
      <section id="gallery" className="py-24 md:py-32 bg-white" data-id="element-733">
        <div className="container mx-auto px-6 md:px-12" data-id="element-734">
          <div className="flex flex-col items-center mb-16" data-id="element-735">
            <h2 className="text-3xl md:text-5xl font-bold text-robeta-black uppercase tracking-editorial mb-10" data-id="element-736">
              Gallery
            </h2>

            <div className="flex space-x-8 border-b border-gray-100 w-full md:w-auto justify-center" data-id="element-737">
              {['interior', 'exterior', 'details'].map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-4 text-xs font-bold uppercase tracking-widest transition-all relative ${activeTab === tab ? 'text-robeta-copper' : 'text-robeta-black/40 hover:text-robeta-black'}`} data-id="element-738">
                  {tab}
                  {activeTab === tab && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-robeta-copper" data-id="element-739" />}
                </button>)}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8" data-id="element-740">
            <AnimatePresence mode="wait" data-id="element-741">
              {activeTab === 'interior' && <>
                  <motion.img initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} src={INTERIOR_1} alt="Interior 1" className="w-full aspect-[4/3] object-cover" data-id="element-742" />
                  <motion.img initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} src={INTERIOR_2} alt="Interior 2" className="w-full aspect-[4/3] object-cover" data-id="element-743" />
                </>}
              {activeTab === 'exterior' && <>
                  <motion.img initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} src={EXTERIOR_1} alt="Exterior 1" className="w-full aspect-[4/3] object-cover" data-id="element-744" />
                  <motion.img initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} src={HERO_IMAGE} alt="Exterior 2" className="w-full aspect-[4/3] object-cover" data-id="element-745" />
                </>}
              {activeTab === 'details' && <>
                  <motion.img initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} src={DETAIL_1} alt="Detail 1" className="w-full aspect-[4/3] object-cover" data-id="element-746" />
                  <motion.img initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} src={DETAIL_2} alt="Detail 2" className="w-full aspect-[4/3] object-cover" data-id="element-747" />
                  <motion.img initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} src={DETAIL_3} alt="Detail 3" className="w-full aspect-[4/3] object-cover" data-id="element-748" />
                  <motion.img initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} src={DETAIL_4} alt="Detail 4" className="w-full aspect-[4/3] object-cover" data-id="element-749" />
                </>}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* 7. HIGHLIGHTS SLIDER */}
      <section id="highlights" className="py-24 md:py-32 bg-robeta-black overflow-hidden" data-id="element-750">
        <div className="container mx-auto px-6 md:px-12 mb-12" data-id="element-751">
          <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-4" data-id="element-752">
            Highlights
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white uppercase tracking-editorial" data-id="element-753">
            Key Features
          </h2>
        </div>

        <div className="flex overflow-x-auto snap-x snap-mandatory pb-12 px-6 md:px-12 gap-6 no-scrollbar" data-id="element-754">
          {[{
          title: 'Silent Travel',
          desc: 'Whisper-quiet journeys with advanced insulation.',
          img: INTERIOR_1
        }, {
          title: 'Air Furniture',
          desc: 'Revolutionary lightweight furniture technology.',
          img: DETAIL_1
        }, {
          title: 'Handcrafted',
          desc: 'Every stitch placed with intention and care.',
          img: DETAIL_2
        }, {
          title: 'Customization',
          desc: 'Over 16,000 configuration possibilities.',
          img: DETAIL_4
        }, {
          title: 'Premium Materials',
          desc: 'Selected for beauty and durability.',
          img: DETAIL_3
        }].map((item, i) => <div key={i} className="flex-shrink-0 w-[85vw] md:w-[40vw] aspect-[16/9] relative snap-center group overflow-hidden" data-id="element-755">
              <img src={item.img} alt={item.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-756" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" data-id="element-757" />
              <div className="absolute bottom-0 left-0 p-8" data-id="element-758">
                <h3 className="text-2xl font-bold text-white uppercase tracking-editorial mb-2" data-id="element-759">
                  {item.title}
                </h3>
                <p className="text-white/70 font-light" data-id="element-760">{item.desc}</p>
              </div>
            </div>)}
        </div>
      </section>

      {/* 8. SPECIFICATIONS */}
      <section id="specifications" className="py-24 md:py-32 bg-white" data-id="element-761">
        <div className="container mx-auto px-6 md:px-12" data-id="element-762">
          <div className="mb-16" data-id="element-763">
            <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-4" data-id="element-764">
              Technical Data
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-robeta-black uppercase tracking-editorial" data-id="element-765">
              Specifications
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 lg:gap-24" data-id="element-766">
            {/* Left Column */}
            <div className="space-y-12" data-id="element-767">
              <div data-id="element-768">
                <h4 className="text-sm font-bold text-robeta-copper uppercase tracking-widest mb-6 border-b border-robeta-copper pb-2" data-id="element-769">
                  Dimensions
                </h4>
                <div className="space-y-4" data-id="element-770">
                  {[['Overall Length', '6.36m'], ['Width', '2.04m'], ['Height', '2.97m'], ['Wheelbase', '3.92m']].map(([label, value]) => <div key={label} className="flex justify-between py-2 border-b border-robeta-black/5" data-id="element-771">
                      <span className="text-robeta-black/50 text-sm uppercase tracking-wide" data-id="element-772">
                        {label}
                      </span>
                      <span className="text-robeta-black font-bold text-sm" data-id="element-773">
                        {value}
                      </span>
                    </div>)}
                </div>
              </div>

              <div data-id="element-774">
                <h4 className="text-sm font-bold text-robeta-copper uppercase tracking-widest mb-6 border-b border-robeta-copper pb-2" data-id="element-775">
                  Weights & Capacities
                </h4>
                <div className="space-y-4" data-id="element-776">
                  {[['GVWR', '3,500 kg'], ['Payload', '~500 kg'], ['Fresh Water', '100L'], ['Grey Water', '90L']].map(([label, value]) => <div key={label} className="flex justify-between py-2 border-b border-robeta-black/5" data-id="element-777">
                      <span className="text-robeta-black/50 text-sm uppercase tracking-wide" data-id="element-778">
                        {label}
                      </span>
                      <span className="text-robeta-black font-bold text-sm" data-id="element-779">
                        {value}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-12" data-id="element-780">
              <div data-id="element-781">
                <h4 className="text-sm font-bold text-robeta-copper uppercase tracking-widest mb-6 border-b border-robeta-copper pb-2" data-id="element-782">
                  Power & Systems
                </h4>
                <div className="space-y-4" data-id="element-783">
                  {[['Solar', '400W'], ['Battery', '200Ah Lithium'], ['Shore Power', '30A'], ['Inverter', '2000W']].map(([label, value]) => <div key={label} className="flex justify-between py-2 border-b border-robeta-black/5" data-id="element-784">
                      <span className="text-robeta-black/50 text-sm uppercase tracking-wide" data-id="element-785">
                        {label}
                      </span>
                      <span className="text-robeta-black font-bold text-sm" data-id="element-786">
                        {value}
                      </span>
                    </div>)}
                </div>
              </div>

              <div data-id="element-787">
                <h4 className="text-sm font-bold text-robeta-copper uppercase tracking-widest mb-6 border-b border-robeta-copper pb-2" data-id="element-788">
                  Chassis & Compliance
                </h4>
                <div className="space-y-4" data-id="element-789">
                  {[['Chassis', 'Mercedes-Benz Sprinter 2500'], ['Engine', '4-Cylinder Diesel'], ['Drive', 'RWD / AWD Optional'], ['Compliance', 'FMVSS / CMVSS / CSA Z240']].map(([label, value]) => <div key={label} className="flex justify-between py-2 border-b border-robeta-black/5" data-id="element-790">
                      <span className="text-robeta-black/50 text-sm uppercase tracking-wide" data-id="element-791">
                        {label}
                      </span>
                      <span className="text-robeta-black font-bold text-sm" data-id="element-792">
                        {value}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-16 flex flex-col sm:flex-row gap-6 justify-center" data-id="element-793">
            <button className="inline-flex items-center justify-center px-8 py-4 border border-robeta-black text-robeta-black font-bold uppercase tracking-widest hover:bg-robeta-black hover:text-white transition-colors text-xs" data-id="element-794">
              <Download className="w-4 h-4 mr-2" data-id="element-795" />
              Spec Sheet (Imperial)
            </button>
            <button className="inline-flex items-center justify-center px-8 py-4 border border-robeta-black text-robeta-black font-bold uppercase tracking-widest hover:bg-robeta-black hover:text-white transition-colors text-xs" data-id="element-796">
              <Download className="w-4 h-4 mr-2" data-id="element-797" />
              Spec Sheet (Metric)
            </button>
          </div>
        </div>
      </section>

      {/* 9. CONVERSION CTA */}
      <section className="py-28 md:py-36 bg-robeta-black text-white text-center" data-id="element-798">
        <div className="container mx-auto px-6 md:px-12" data-id="element-799">
          <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold uppercase tracking-editorial mb-12" data-id="element-800">
            Experience Mythos 4 Two
            <br data-id="element-801" />
            in Person
          </h2>
          <div className="flex flex-col sm:flex-row justify-center gap-6" data-id="element-802">
            <Link to="/dealer-network" className="px-10 py-4 bg-robeta-copper text-robeta-black font-bold uppercase tracking-widest hover:bg-white transition-colors min-w-[200px]" data-id="element-803">
              Find a Dealer
            </Link>
            <Link to="/contact" className="px-10 py-4 border border-white text-white font-bold uppercase tracking-widest hover:bg-white/10 transition-colors min-w-[200px]" data-id="element-804">
              Request Info
            </Link>
          </div>
        </div>
      </section>
    </main>;
}